import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Defaults } from '@pbt/pbt-ui-components'

import type { Estimate as GraphqlEstimate } from '~/api/graphql/generated/types'
import type { PuiDataTableColumn } from '~/components/common/lists/pui-data-table/puiDataTableType'
import {
  fetchEstimatePage,
  fetchMoreItemsForClientEstimateActivity,
  getClientBillingActivityEstimateError,
  getEstimateLoading,
  getEstimatePageData,
  getEstimateTotalCount,
  getShouldRefreshData,
} from '~/store/duck/clientBillingActivityData'

import {
  getExpandedWidgetLimit,
  getExpandedWidgetOffset,
} from '../table/common/expandedWidgetUtils'
import { useGetEstimateBillingActivityColumns } from '../table/estimates/EstimateBillingActivityTable'
import {
  ClientBillingActivityInfiniteLoaderList,
  type ClientBillingActivityInfiniteLoaderListClassesType,
} from './ClientBillingActivityInfiniteLoaderList'
import { useFetchOnInitialSyncQueryParams } from './hooks'

interface EstimatesInfiniteLoaderListProps {
  classes?: ClientBillingActivityInfiniteLoaderListClassesType
  clientId: string | undefined
  initialItem?: number
  onClickEstimate: (data: GraphqlEstimate) => void
  supportBiDirectionalInfiniteScroll?: boolean
}

export const EstimatesInfiniteLoaderList = ({
  classes,
  clientId,
  onClickEstimate,
  initialItem: initialItemParam = 0,
  supportBiDirectionalInfiniteScroll = false,
}: EstimatesInfiniteLoaderListProps) => {
  const dispatch = useDispatch()
  const { t } = useTranslation('Common')

  const refresh = useSelector(getShouldRefreshData)
  const estimatePageData = useSelector(getEstimatePageData)
  const estimateTotalCount = useSelector(getEstimateTotalCount)
  const isLoading = useSelector(getEstimateLoading)
  const error = useSelector(getClientBillingActivityEstimateError)

  const [offsetValue, setOffsetValue] = useState(0)

  const handleFetchMore = ({ from, to }: { from: number; to: number }) => {
    if (clientId) {
      const offset = from < 10 && supportBiDirectionalInfiniteScroll ? 0 : from
      const limit =
        to && supportBiDirectionalInfiniteScroll
          ? to - offset + 1
          : Defaults.INFINITE_LIST_BATCH_LOAD_COUNT
      setOffsetValue(offset)
      dispatch(
        fetchMoreItemsForClientEstimateActivity({
          clientId,
          from: offset,
          to: limit,
        }),
      )
    }
  }

  useEffect(() => {
    if (clientId && refresh) {
      const limit = initialItemParam
        ? Defaults.INFINITE_LIST_BATCH_LOAD_COUNT * 2
        : Defaults.INFINITE_LIST_BATCH_LOAD_COUNT
      dispatch(
        fetchEstimatePage({
          clientId,
          offset: offsetValue,
          limit,
          isRefreshing: true,
        }),
      )
    }
  }, [clientId, refresh])

  // Fetch initial data with proper invoice params when necessary
  useFetchOnInitialSyncQueryParams({
    key: 'Estimates',
    onFetch: () => {
      if (!clientId) {
        return
      }
      const offset = getExpandedWidgetOffset(
        initialItemParam,
        supportBiDirectionalInfiniteScroll,
      )
      const limit = getExpandedWidgetLimit(
        initialItemParam,
        supportBiDirectionalInfiniteScroll,
      )
      setOffsetValue(offset)
      dispatch(
        fetchEstimatePage({
          clientId,
          offset,
          limit,
          fillPrecedingWithNull:
            offset > 0 && supportBiDirectionalInfiniteScroll,
        }),
      )
    },
  })

  return (
    <ClientBillingActivityInfiniteLoaderList
      classes={classes}
      clientId={clientId}
      columns={
        useGetEstimateBillingActivityColumns(
          'value',
          onClickEstimate,
          supportBiDirectionalInfiniteScroll,
        ) as PuiDataTableColumn[]
      }
      emptyPlaceholder={t('Common:NO_ESTIMATES')}
      error={error}
      initialItemParam={initialItemParam}
      isLoadingInitialData={isLoading}
      pageData={estimatePageData}
      totalCount={estimateTotalCount}
      onFetchMore={handleFetchMore}
    />
  )
}
